import { useState, useEffect, useRef } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MessageCircle, Send, ArrowLeft, Smile, Plus, Search, X, Loader2, MoreVertical, CheckCheck, Circle } from "lucide-react";
import { getConversations, getMessages, sendMessage, getUserProfile, searchUsers, markConversationRead, markConversationUnread } from "@/lib/api";
import { toast } from "sonner";
import CatholicEmojiPicker, { CATHOLIC_EMOJIS } from "@/components/CatholicEmojiPicker";

// Helper component to render text with Catholic emojis
function RenderContent({ content, isSentByMe = false }) {
  if (!content) return null;
  
  // First, convert URLs to clickable links
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  
  // Link styling based on message sender (white for sent messages on purple bg, purple for received)
  const linkClassName = isSentByMe 
    ? "text-white font-semibold underline hover:opacity-80 break-all"
    : "text-[var(--liturgical-primary)] underline hover:brightness-90 break-all";
  
  // Handle both Catholic emojis and URLs
  const processContent = (text) => {
    // Split by URLs first
    const urlParts = text.split(urlRegex);
    return urlParts.map((part, idx) => {
      if (part.match(urlRegex)) {
        return (
          <a 
            key={`url-${idx}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClassName}
          >
            {part}
          </a>
        );
      }
      
      // Check for Catholic emojis in this text segment
      if (part.includes("[catholic:")) {
        const catholicEmojiMap = {};
        CATHOLIC_EMOJIS.forEach(e => {
          catholicEmojiMap[e.id] = e;
        });
        
        const emojiParts = part.split(/(\[catholic:\w+\])/g);
        return emojiParts.map((ePart, eIdx) => {
          const match = ePart.match(/\[catholic:(\w+)\]/);
          if (match) {
            const emoji = catholicEmojiMap[match[1]];
            if (emoji) {
              return <img key={`emoji-${idx}-${eIdx}`} src={emoji.url} alt={emoji.name} className="inline-block w-5 h-5 align-text-bottom" />;
            }
          }
          return <span key={`text-${idx}-${eIdx}`}>{ePart}</span>;
        });
      }
      
      return <span key={`plain-${idx}`}>{part}</span>;
    });
  };
  
  // Split by newlines to preserve line breaks
  const lines = content.split('\n');
  return (
    <>
      {lines.map((line, lineIdx) => (
        <span key={`line-${lineIdx}`}>
          {processContent(line)}
          {lineIdx < lines.length - 1 && <br />}
        </span>
      ))}
    </>
  );
}

function ConversationList({ conversations, activeId, onSelect, onMarkRead, onMarkUnread }) {
  if (conversations.length === 0) {
    return (
      <div className="text-center py-16 px-4">
        <MessageCircle className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
        <p className="text-sm text-[#6B665E] font-['Nunito']">No conversations yet</p>
        <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">Click "New" to start a conversation</p>
      </div>
    );
  }

  return (
    <div className="py-1">
      {conversations.map(c => (
        <div
          key={c.user.user_id}
          className={`mx-2 mb-1 p-2 rounded-md transition-colors cursor-pointer ${
            activeId === c.user.user_id ? 'bg-[var(--liturgical-primary)]/10' : 'hover:bg-[#F0EBE0]'
          }`}
          data-testid={`conversation-${c.user.user_id}`}
        >
          {/* Grid layout: avatar | name/message | menu */}
          <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr 32px', gap: '12px', alignItems: 'center' }}>
            {/* Avatar */}
            <div className="relative" onClick={() => onSelect(c.user)}>
              <Avatar className="h-10 w-10 border border-[#E5E0D5]">
                <AvatarImage src={c.user.picture} />
                <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-sm font-['Nunito']">
                  {c.user.name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {c.unread > 0 && (
                <span className="absolute -top-1 -right-1 bg-[var(--liturgical-primary)] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {c.unread > 9 ? "9+" : c.unread}
                </span>
              )}
            </div>
            
            {/* Name and message */}
            <div className="overflow-hidden" onClick={() => onSelect(c.user)}>
              <p className={`font-['Nunito'] text-sm truncate ${c.unread > 0 ? 'font-bold text-[#2D2A26]' : 'font-semibold text-[#2D2A26]'}`}>
                {c.user.name}
              </p>
              <p className={`text-xs font-['Nunito'] truncate ${c.unread > 0 ? 'text-[#2D2A26] font-medium' : 'text-[#6B665E]'}`}>
                {c.last_message}
              </p>
            </div>
            
            {/* Menu button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="w-8 h-8 flex items-center justify-center rounded text-[#6B665E] hover:text-[#2D2A26] hover:bg-[#E5E0D5]"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`conversation-menu-${c.user.user_id}`}
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border border-[#E5E0D5] shadow-lg z-50">
                {c.unread > 0 ? (
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      onMarkRead(c.user.user_id);
                    }}
                    className="font-['Nunito'] text-sm cursor-pointer"
                  >
                    <CheckCheck className="w-4 h-4 mr-2" />
                    Mark as read
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      onMarkUnread(c.user.user_id);
                    }}
                    className="font-['Nunito'] text-sm cursor-pointer"
                  >
                    <Circle className="w-4 h-4 mr-2" />
                    Mark as unread
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      ))}
    </div>
  );
}

function NewConversationDialog({ open, onOpenChange, onSelectUser, currentUserId }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSearchResults([]);
    }
  }, [open]);

  useEffect(() => {
    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchUsers(searchQuery, { limit: 10 });
        // Filter out current user from results
        const users = res.data?.users || res.data || [];
        const filtered = users.filter(u => u.user_id !== currentUserId);
        setSearchResults(filtered);
      } catch (err) {
        console.error("Search failed:", err);
        toast.error("Failed to search users");
      }
      setSearching(false);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, currentUserId]);

  const handleSelectUser = (user) => {
    onSelectUser(user);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-conversation-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Cinzel'] text-lg text-[#2D2A26]">New Conversation</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name..."
              className="pl-9 pr-9 border-[#E5E0D5] font-['Nunito'] focus-visible:ring-[var(--liturgical-primary)]"
              autoFocus
              data-testid="new-conversation-search"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B665E] hover:text-[#2D2A26]"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Search Results */}
          <div className="min-h-[200px] max-h-[300px] overflow-y-auto">
            {searching ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" />
              </div>
            ) : searchQuery.trim().length < 2 ? (
              <div className="text-center py-8">
                <Search className="w-8 h-8 text-[#E5E0D5] mx-auto mb-2" />
                <p className="text-sm text-[#6B665E] font-['Nunito']">Type at least 2 characters to search</p>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-[#6B665E] font-['Nunito']">No users found</p>
              </div>
            ) : (
              <div className="space-y-1">
                {searchResults.map((user) => (
                  <button
                    key={user.user_id}
                    onClick={() => handleSelectUser(user)}
                    className="w-full flex items-center gap-3 p-3 rounded-md text-left hover:bg-[#F0EBE0] transition-colors"
                    data-testid={`search-result-${user.user_id}`}
                  >
                    <Avatar className="h-10 w-10 border border-[#E5E0D5]">
                      <AvatarImage src={user.picture || user.profile_picture} />
                      <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-sm font-['Nunito']">
                        {user.name?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-['Nunito'] text-sm font-semibold text-[#2D2A26] truncate">{user.name}</p>
                      {user.parish_name && (
                        <p className="text-xs text-[#6B665E] font-['Nunito'] truncate">{user.parish_name}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChatView({ currentUser, otherUser, onBack }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const emojiPickerRef = useRef(null);

  const loadMessages = async () => {
    try {
      const res = await getMessages(otherUser.user_id);
      setMessages(res.data);
    } catch {}
  };

  useEffect(() => {
    loadMessages();
    pollRef.current = setInterval(loadMessages, 5000);
    return () => clearInterval(pollRef.current);
  }, [otherUser.user_id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEmojiClick = (emojiObject) => {
    setNewMessage(prev => prev + emojiObject.emoji);
    setShowEmojiPicker(false);
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const res = await sendMessage({ receiver_id: otherUser.user_id, content: newMessage });
      setMessages([...messages, res.data]);
      setNewMessage("");
    } catch {
      toast.error("Failed to send message");
    }
    setSending(false);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header */}
      <div className="flex items-center gap-3 p-4 border-b border-[#E5E0D5] bg-white">
        <button onClick={onBack} className="md:hidden p-1 rounded hover:bg-[#F0EBE0]" data-testid="chat-back-btn">
          <ArrowLeft className="w-5 h-5 text-[#2D2A26]" />
        </button>
        <Avatar className="h-9 w-9 border border-[#E5E0D5]">
          <AvatarImage src={otherUser.picture} />
          <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-sm">{otherUser.name?.[0]?.toUpperCase()}</AvatarFallback>
        </Avatar>
        <div>
          <p className="font-['Nunito'] text-sm font-semibold text-[#2D2A26]">{otherUser.name}</p>
          {otherUser.parish_name && <p className="text-[10px] text-[var(--liturgical-secondary)] font-['Nunito']">{otherUser.parish_name}</p>}
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-3">
          {messages.map((msg) => {
            const isMine = msg.sender_id === currentUser.user_id;
            return (
              <div key={msg.message_id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] px-3.5 py-2 rounded-lg text-sm font-['Nunito'] ${
                  isMine ? 'bg-[var(--liturgical-primary)] text-white rounded-br-none' : 'bg-[#F0EBE0] text-[#2D2A26] rounded-bl-none'
                }`} data-testid={`message-${msg.message_id}`}>
                  <RenderContent content={msg.content} isSentByMe={isMine} />
                  <div className={`text-[10px] mt-1 ${isMine ? 'text-white/60' : 'text-[#6B665E]'}`}>
                    {new Date(msg.created_at).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-4 border-t border-[#E5E0D5] bg-white">
        <div className="flex gap-2 items-end">
          <div className="relative self-end" ref={emojiPickerRef}>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)} 
              className="h-9 w-9 p-0 text-[#6B665E] hover:text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/5"
              data-testid="message-emoji-btn"
            >
              <Smile className="w-5 h-5" />
            </Button>
            {showEmojiPicker && (
              <div className="absolute bottom-12 left-0 z-50">
                <CatholicEmojiPicker 
                  onEmojiClick={handleEmojiClick} 
                  width={320} 
                  height={380}
                />
              </div>
            )}
          </div>
          <Textarea
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 min-h-[44px] max-h-[200px] resize-none border-[#E5E0D5] font-['Nunito'] text-sm focus-visible:ring-[var(--liturgical-primary)] overflow-y-auto"
            rows={1}
            onInput={e => {
              // Auto-expand textarea height
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
            }}
            data-testid="message-input"
          />
          <Button 
            onClick={handleSend} 
            disabled={sending || !newMessage.trim()} 
            className="bg-[var(--liturgical-primary)] hover:brightness-90 active:scale-95 transition-all self-end h-10"
            data-testid="message-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function MessagesPage({ user }) {
  const [searchParams] = useSearchParams();
  const { userId: pathUserId } = useParams();
  const [conversations, setConversations] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewConversation, setShowNewConversation] = useState(false);

  // Get target user ID from either query param (?to=xxx) or path param (/messages/xxx)
  const targetUserId = searchParams.get('to') || pathUserId;

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load conversations
        const convRes = await getConversations();
        setConversations(convRes.data);
        
        // If we have a target user ID, try to open that conversation
        if (targetUserId) {
          // First check if we already have a conversation with this user
          const existingConv = convRes.data.find(c => c.user.user_id === targetUserId);
          if (existingConv) {
            setActiveUser(existingConv.user);
          } else {
            // No existing conversation - fetch user info to start a new one
            try {
              const userRes = await getUserProfile(targetUserId);
              // The API returns { user: {...}, posts: [...], listings: [...] }
              const userData = userRes.data?.user || userRes.data;
              if (userData && userData.user_id) {
                setActiveUser({
                  user_id: userData.user_id,
                  name: userData.name,
                  picture: userData.picture
                });
              } else {
                console.error("User data not found in response:", userRes.data);
                toast.error("Could not find user to message");
              }
            } catch (err) {
              console.error("Failed to load user:", err);
              toast.error("Could not find user to message");
            }
          }
        }
      } catch (err) {
        console.error("Failed to load conversations:", err);
      }
      setLoading(false);
    };
    
    loadData();
  }, [targetUserId]);

  const loadConversations = async () => {
    try {
      const convRes = await getConversations();
      setConversations(convRes.data);
    } catch (err) {
      console.error("Failed to load conversations:", err);
    }
  };

  const handleNewConversationSelect = (selectedUser) => {
    setActiveUser({
      user_id: selectedUser.user_id,
      name: selectedUser.name,
      picture: selectedUser.picture || selectedUser.profile_picture,
      parish_name: selectedUser.parish_name
    });
  };

  const handleMarkRead = async (userId) => {
    try {
      await markConversationRead(userId);
      // Update local state
      setConversations(prev => prev.map(c => 
        c.user.user_id === userId ? { ...c, unread: 0 } : c
      ));
      toast.success("Marked as read");
    } catch (err) {
      toast.error("Failed to mark as read");
    }
  };

  const handleMarkUnread = async (userId) => {
    try {
      await markConversationUnread(userId);
      // Update local state - set unread to 1 to indicate unread
      setConversations(prev => prev.map(c => 
        c.user.user_id === userId ? { ...c, unread: Math.max(1, c.unread) } : c
      ));
      toast.success("Marked as unread");
    } catch (err) {
      toast.error("Failed to mark as unread");
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F7F2]" data-testid="messages-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6">
        <h1 className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26] tracking-tight mb-6">Messages</h1>
        <div className="bg-white border border-[#E5E0D5] rounded-lg shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
          <div className="flex h-full">
            {/* Sidebar */}
            <div 
              className={`border-r border-[#E5E0D5] flex flex-col ${activeUser ? 'hidden md:flex' : 'flex'}`}
              style={{ width: '300px', flexShrink: 0 }}
            >
              <div className="p-4 border-b border-[#E5E0D5] flex items-center justify-between">
                <h2 className="font-['Nunito'] text-sm font-semibold text-[#2D2A26]">Conversations</h2>
                <Button
                  size="sm"
                  onClick={() => setShowNewConversation(true)}
                  className="bg-[var(--liturgical-primary)] hover:brightness-90 h-8 px-3 font-['Nunito'] text-xs"
                  data-testid="new-conversation-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  New
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="text-center py-8 text-[#6B665E] text-sm">Loading...</div>
                ) : (
                  <ConversationList 
                    conversations={conversations} 
                    activeId={activeUser?.user_id} 
                    onSelect={setActiveUser}
                    onMarkRead={handleMarkRead}
                    onMarkUnread={handleMarkUnread}
                  />
                )}
              </div>
            </div>

            {/* Chat */}
            <div className={`flex-1 ${!activeUser ? 'hidden md:flex' : 'flex'} flex-col`} style={{ minWidth: 0 }}>
              {activeUser ? (
                <ChatView currentUser={user} otherUser={activeUser} onBack={() => setActiveUser(null)} />
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageCircle className="w-12 h-12 text-[#E5E0D5] mx-auto mb-3" />
                    <p className="text-[#6B665E] font-['Nunito']">Select a conversation or start a new one</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* New Conversation Dialog */}
      <NewConversationDialog
        open={showNewConversation}
        onOpenChange={setShowNewConversation}
        onSelectUser={handleNewConversationSelect}
        currentUserId={user?.user_id}
      />
    </div>
  );
}
